import React from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Card, H2, Separator, Text } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { CoordinatedAccountRow } from './components/CoordinatedAccountRow';
import { CoordinatedAccountsCardDocument } from './graphql/coordinatedAccountsCard.generated';

import { trackClick } from '../../../features/track';
import { urlBuilder } from '../../../Shared/urlBuilder';

const CoordinatedAccountsCardView: React.FC = () => {
  const returnToPath = window.location.pathname;
  const accountId = useRequiredParam('accountId');
  const { data } = useGraphQLQuery(CoordinatedAccountsCardDocument, {
    variables: { id: accountId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  const { account } = data;
  if (account.__typename !== 'ManagedAccount') return null;

  const { subAccounts, legacyGoalId } = account;
  if (subAccounts.length <= 1) return null;

  const addAccountUrl = urlBuilder.appGoalTaxLocationChangeNew.absolute({
    goalId: legacyGoalId,
    return_to: returnToPath,
  });

  return (
    <Card>
      <Card.Header
        css={{
          padding: 'space.300',
          display: 'flex',
          gap: 'space.50',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <H2 variant="title200">Tax-coordinated accounts</H2>
        <Text variant="text50" color="text.weak">
          Balance
        </Text>
      </Card.Header>
      <Card.Content>
        {subAccounts.map(subAccount => (
          <CoordinatedAccountRow key={subAccount.id} subAccount={subAccount} />
        ))}
      </Card.Content>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Footer css={{ padding: 'space.300' }}>
        <SambaAutoLink to={addAccountUrl} onClick={() => trackClick({ name: 'add_account_coordinated_accounts_card' })}>
          Add new account
        </SambaAutoLink>
      </Card.Footer>
    </Card>
  );
};

export const CoordinatedAccountsCard: React.FC = () => (
  <ErrorBoundary fallbackType="none">
    <CoordinatedAccountsCardView />
  </ErrorBoundary>
);
